import { useEffect, useRef, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ApiError } from "@corenote/api-client";
import { useSession } from "./session";

// Transport-level resets drop the occasional refetch under bursty parallel
// loads (CORE-210 staging e2e). Retrying a READ is safe; retrying a WRITE
// would replay an unkeyed mutation, so mutations always fail through and
// keep their drafts on the rejected post. Definitive 4xx answers (404,
// 401, 403, ...) are verdicts, never retried.
const TRANSIENT_STATUSES = [408, 500, 502, 503, 504];

function isTransientFailure(error: unknown): boolean {
  if (error instanceof ApiError) {
    return TRANSIENT_STATUSES.includes(error.status);
  }
  return error instanceof TypeError;
}

function createClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        retry: (failures, error) =>
          failures < 2 && isTransientFailure(error),
        // Short first backoff so a single dropped refetch self-heals within
        // a second; the second gap widens before the final attempt.
        retryDelay: (attempt) => (attempt === 0 ? 300 : 1_000),
        refetchOnWindowFocus: false,
      },
      mutations: {
        // Never auto-replay writes; the caller keeps its state and decides.
        retry: false,
      },
    },
  });
}

export function SessionQueryProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const tenant = session?.tenant_id;
  const principal = session?.principal_id;
  const credential = session?.csrf_token;
  const [scope, setScope] = useState(() => ({
    tenant,
    principal,
    credential,
    generation: 0,
    client: createClient(),
  }));
  // Adjust before committing children: no render under a new identity may
  // observe a still-fresh cache from its predecessor. A numeric React key
  // remounts observers without putting a credential in the component key.
  if (
    scope.tenant !== tenant ||
    scope.principal !== principal ||
    scope.credential !== credential
  ) {
    setScope({
      tenant,
      principal,
      credential,
      generation: scope.generation + 1,
      client: createClient(),
    });
  }
  const previousClient = useRef(scope.client);
  useEffect(() => {
    const previous = previousClient.current;
    previousClient.current = scope.client;
    if (previous !== scope.client) {
      void previous.cancelQueries();
      previous.clear();
    }
  }, [scope.client]);
  // Old mutation completions retain their OLD client; even a completion
  // after logout cannot seed the newly authorized account's cache.
  return (
    <QueryClientProvider key={scope.generation} client={scope.client}>
      {children}
    </QueryClientProvider>
  );
}
